const _excluded = ["ref"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import { Controller, useForm } from 'react-hook-form';
import { Button, Callout, CustomModal, TableList, TextField, Typography, useOverlayContext } from 'seagull';
import { getRequiredValidator, richTextValidation, titleValidation } from 'validators';
import { handlePromise, sendErrReport } from "../../infra/utils";
import { helpArticles } from "../../utils/helpArticles";
import { useStoreActions } from "../../store/store-context";
import styles from "./AddCreditModal.module.scss";
import Decimal from 'decimal.js';
import { MoneyModel } from "../../models";
import ExternalLinkButton from "../ExternalLinkButton";
import { useBillingObservable } from "../../providers/BillingObservableProvider";
import { ServiceTemplateInput } from "../common/ServiceTemplateInput/ServiceTemplateInput";
import { useAddCredit } from "./hooks/useAddCredit";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const testPrefix = 'add-credit-modal';
const headerItems = [{
  value: 'Service name'
}, {
  value: 'Total'
}];
const columnDefs = [{
  width: 450
}, {
  width: 125
}];
const getShouldShowFieldError = (fieldState, formState) => fieldState.error && (fieldState.isTouched || formState.isSubmitted);
export default function AddCreditModal(_ref) {
  let {
    dismissModal,
    relationship,
    nextBillingDate
  } = _ref;
  const {
    addCredit
  } = useAddCredit();
  const {
    control,
    formState,
    handleSubmit,
    setValue
  } = useForm({
    mode: 'all',
    defaultValues: {
      note: '',
      price: null
    }
  });
  const overlayContext = useOverlayContext();
  const {
    logBI
  } = useStoreActions();
  const {
    notifyToRefresh
  } = useBillingObservable();
  const applyCredit = async _ref2 => {
    var _price$amount;
    let {
      name,
      note,
      price,
      serviceTemplateId
    } = _ref2;
    if (!nextBillingDate) return;
    logBI(18001, {
      title: name,
      note,
      price: price === null || price === void 0 ? void 0 : price.toNumber()
    });
    const loaderName = 'addCreditLoader';
    overlayContext.startLoader(loaderName);
    const rate = (_price$amount = price === null || price === void 0 ? void 0 : price.amount()) !== null && _price$amount !== void 0 ? _price$amount : new Decimal(0);
    const result = await handlePromise(addCredit({
      data: {
        amount: rate.toString(),
        note,
        serviceTemplateId
      },
      relationshipId: relationship.id
    }));
    if (!result.success) {
      overlayContext.showErrorToast('Failed to add credit due to an unexpected error', "".concat(testPrefix, "-error-toast"));
      sendErrReport({
        type: 'chargeError',
        error: result.error
      });
    } else {
      overlayContext.showSuccessToast("".concat(price ? price.abs().display() : '', " credit was added to ").concat(relationship.customer.name), "".concat(testPrefix, "-success-toast"));
      notifyToRefresh();
      dismissModal({
        status: 'success'
      });
    }
    overlayContext.stopLoader(loaderName);
  };
  return /*#__PURE__*/_jsx(CustomModal, {
    className: styles.modal,
    header: /*#__PURE__*/_jsx("div", {
      "data-testid": "".concat(testPrefix, "-header"),
      className: styles.header,
      children: /*#__PURE__*/_jsx(Typography, {
        variant: "h6",
        weight: "bold",
        children: "Add Credit"
      })
    }),
    footer: /*#__PURE__*/_jsx("div", {
      className: styles.actions,
      children: /*#__PURE__*/_jsx(Button, {
        type: "submit",
        "data-testid": "".concat(testPrefix, "-add-credit-button"),
        loading: formState.isSubmitting,
        form: testPrefix,
        children: "Add credit"
      })
    }),
    width: "784px",
    height: "524px",
    "data-testid": testPrefix,
    onClose: () => dismissModal({
      status: 'canceled'
    }),
    headerClassName: styles.blueHeader,
    children: /*#__PURE__*/_jsxs("form", {
      onSubmit: handleSubmit(applyCredit),
      id: testPrefix,
      className: styles.content,
      children: [/*#__PURE__*/_jsxs(Callout, {
        children: ["Credit added goes towards the associated agreement and gets applied automatically to future invoices.", ' ', /*#__PURE__*/_jsx(ExternalLinkButton, {
          openInNewTab: true,
          underline: "none",
          href: helpArticles.applyClientCredit(),
          children: "Learn more about how credits work"
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: styles.formFields,
        children: [/*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsxs(TableList.Provider, {
            columnDefs: columnDefs,
            children: [/*#__PURE__*/_jsx(TableList.Header, {
              items: headerItems
            }), /*#__PURE__*/_jsx(TableList.Row, {
              items: [{
                value: /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: "serviceTemplateId",
                  rules: titleValidation('', {
                    required: true
                  }),
                  render: renderProps => /*#__PURE__*/_jsx(ServiceTemplateInput, {
                    relationship: relationship,
                    renderProps: renderProps,
                    testId: "".concat(testPrefix, "-dropdown-service-selector"),
                    applyServiceChange: serviceTemplate => {
                      var _serviceTemplate$name;
                      setValue('name', (_serviceTemplate$name = serviceTemplate === null || serviceTemplate === void 0 ? void 0 : serviceTemplate.name) !== null && _serviceTemplate$name !== void 0 ? _serviceTemplate$name : '');
                    }
                  })
                })
              }, {
                value: /*#__PURE__*/_jsx(Controller, {
                  control: control,
                  name: "price",
                  rules: {
                    validate: {
                      required: getRequiredValidator(),
                      positiveAmount: value => !!value && value.amount().gt(0)
                    }
                  },
                  render: _ref3 => {
                    let {
                      field: {
                        value,
                        ref,
                        onBlur,
                        onChange: _onChange
                      },
                      fieldState,
                      formState
                    } = _ref3;
                    return /*#__PURE__*/_jsx(TextField, {
                      isMoney: true,
                      size: "small",
                      prefix: "$",
                      placeholder: "0.00",
                      value: value != null ? value.toFixed() : '',
                      inputRef: ref,
                      onBlur: onBlur,
                      testid: "".concat(testPrefix, "-cost-input"),
                      error: getShouldShowFieldError(fieldState, formState),
                      onChange: e => {
                        const dollarsAsString = e.currentTarget.value;
                        const newValue = dollarsAsString ? new MoneyModel(new Decimal(dollarsAsString)) : null;
                        _onChange(newValue);
                      }
                    });
                  }
                })
              }]
            })]
          })
        }), /*#__PURE__*/_jsx(Controller, {
          control: control,
          name: "note",
          rules: richTextValidation('Note'),
          render: _ref4 => {
            let {
                field: {
                  ref
                },
                fieldState
              } = _ref4,
              restField = _objectWithoutProperties(_ref4.field, _excluded);
            return /*#__PURE__*/_jsx(TextField, _objectSpread(_objectSpread({}, restField), {}, {
              "data-testid": "".concat(testPrefix, "-note-input"),
              label: "Note (optional)",
              multiline: true,
              minRows: 3,
              maxRows: 3,
              inputRef: ref,
              error: getShouldShowFieldError(fieldState, formState),
              helperText: getShouldShowFieldError(fieldState, formState) && fieldState.error.message
            }));
          }
        })]
      })]
    })
  });
}